/**
 * Query-string reading for params that arrive on the marketing-site links.
 *
 * Why we read `window.location.search` AND the hash tail instead of using
 * Angular's `ActivatedRoute.queryParams`:
 *
 *   The app uses `HashLocationStrategy`, which means Angular only sees the
 *   query string after the `#` (e.g. `#/register?foo=bar`). Params from the
 *   marketing-site `/register` link flow through the salonmonster.com
 *   htaccess redirect and land in `window.location.search` (before the `#`),
 *   so reading `ActivatedRoute.queryParams` drops them entirely.
 *
 * We defensively parse the post-`#` query as well so edge-case rewrites
 * that put params after the fragment still work. When the same key appears
 * in both, `search` wins — that is the canonical location for marketing.
 */

/**
 * Read the given keys off the search + hash queries, `search` taking
 * precedence. Missing keys come back as `null` rather than absent, so callers
 * get a fully-populated record.
 */
export function readParams<K extends string> (
  search: string,
  hash: string,
  keys: ReadonlyArray<K>
): Record<K, string | null> {
  const fromSearch = parseQueryString(stripLeadingQuestionMark(search))
  const fromHash = parseQueryString(extractQueryFromHash(hash))

  const out = {} as Record<K, string | null>
  for (const key of keys) {
    out[key] = fromSearch[key] ?? fromHash[key] ?? null
  }
  return out
}

function stripLeadingQuestionMark (value: string): string {
  return value && value.charAt(0) === '?' ? value.slice(1) : value || ''
}

function extractQueryFromHash (hash: string): string {
  if (!hash) {
    return ''
  }
  const qIdx = hash.indexOf('?')
  return qIdx >= 0 ? hash.slice(qIdx + 1) : ''
}

function parseQueryString (qs: string): Record<string, string> {
  const out: Record<string, string> = {}
  if (!qs) {
    return out
  }
  for (const pair of qs.split('&')) {
    if (!pair) {
      continue
    }
    const eqIdx = pair.indexOf('=')
    const rawKey = eqIdx >= 0 ? pair.slice(0, eqIdx) : pair
    const rawVal = eqIdx >= 0 ? pair.slice(eqIdx + 1) : ''
    try {
      const key = decodeURIComponent(rawKey.replace(/\+/g, ' '))
      const value = decodeURIComponent(rawVal.replace(/\+/g, ' '))
      if (key && !(key in out)) {
        out[key] = value
      }
    } catch (_) {
      // Malformed encoding — skip this pair.
    }
  }
  return out
}
